/**
 * Statistics manager — persists game statistics to localStorage.
 *
 * Follows the same pattern as settingsManager.ts and stateManager.ts.
 */

import { GameStatistics, defaultStatistics } from '../Models/Statistics'

const STATS_KEY = 'solitaire-statistics'

// ─── In-memory cache ────────────────────────────────────────────────────────
// Avoids re-parsing localStorage on every menu paint frame (~60fps).
// Invalidated whenever saveStatistics writes new data.

let _cachedStats: GameStatistics | null = null

// ─── Persistence ────────────────────────────────────────────────────────────

export function saveStatistics(stats: GameStatistics): void {
    try {
        localStorage.setItem(STATS_KEY, JSON.stringify(stats))
        // Update in-memory cache so subsequent reads are instant
        _cachedStats = { ...stats }
    } catch (error) {
        console.error('Failed to save statistics:', error)
    }
}

export function loadStatistics(): GameStatistics {
    if (_cachedStats) return _cachedStats

    try {
        const stored = localStorage.getItem(STATS_KEY)
        if (stored) {
            const parsed = JSON.parse(stored)
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                console.warn('Stored statistics failed validation, using defaults')
                _cachedStats = { ...defaultStatistics }
                return _cachedStats
            }
            // Merge with defaults to handle new fields added in updates
            _cachedStats = { ...defaultStatistics, ...parsed }
            return _cachedStats
        }
    } catch (error) {
        console.warn('Failed to load statistics, using defaults:', error)
    }
    _cachedStats = { ...defaultStatistics }
    return _cachedStats
}

/**
 * Record a game win.
 * @param timeMs — The time taken to win (in milliseconds)
 */
export function recordGameWin(timeMs: number): void {
    const stats = loadStatistics()
    stats.gamesPlayed++
    stats.gamesWon++
    stats.totalPlayTime += timeMs

    // Best time
    if (stats.bestTime === null || timeMs < stats.bestTime) {
        stats.bestTime = timeMs
    }

    // Average win time
    if (stats.averageWinTime === null) {
        stats.averageWinTime = timeMs
    } else {
        // Running average: ((old_avg * (wins-1)) + new_time) / wins
        stats.averageWinTime = Math.round(
            (stats.averageWinTime * (stats.gamesWon - 1) + timeMs) / stats.gamesWon
        )
    }

    // Streaks
    stats.currentStreak++
    if (stats.currentStreak > stats.longestStreak) {
        stats.longestStreak = stats.currentStreak
    }

    saveStatistics(stats)
}

/**
 * Record a game loss (player started a new game without winning).
 * Resets the current win streak.
 */
export function recordGameLoss(): void {
    const stats = loadStatistics()
    stats.gamesPlayed++
    stats.currentStreak = 0
    saveStatistics(stats)
}
